/**
 * État de COLLECTE d'un compteur électrique (PDL), tel que la carte compteur
 * l'affiche : une pastille et un libellé court (DEV-1620).
 *
 * ## Le défaut corrigé
 *
 * La carte compteur affichait « Actif » sur la seule foi de l'état
 * CONTRACTUEL du point (`informations.pdl.etat === "en_service"`) et de l'état
 * du CONSENTEMENT (`consentement.etat === "active"`). Aucun de ces deux faits
 * ne parle de la collecte. Mesuré en prod le 2026-09-04 à 11:50:43Z sur le PRM
 * 50013169271008 (compte homeys, bâtiment 692ffeac0f28d5945f3d9832) : la carte
 * annonçait « Actif » alors que dapi servait, sur le MÊME objet source,
 * `data_retrieval.status = {a_jour: false, cause: "donnee_non_livree",
 * a_qui_dagir: "enedis", derniere_mesure: "2026-08-24", retard_jours: 11,
 * attendu_jours: 3}`. Onze jours de décrochage, et le client devait ouvrir la
 * page Données pour l'apprendre.
 *
 * ## La source de vérité, et pourquoi c'est celle-là
 *
 * `informations.pdl.data_retrieval.status` est le bloc CLIENT du verdict
 * enedis, projeté une seule fois côté serveur (`substrate/projection_client.py`,
 * décision W7 / DEV-1591) et recopié TEL QUEL par dapi
 * (`modules/source/data_retrieval_enedis.py`). C'est le même verdict que celui
 * dont la bannière de la page Données est une lecture : api-metier
 * (`workflow.enedis.verdict.js`) en tire le libellé `historique_*` que
 * `useWorkflowEnedisMessage.ts` habille. Carte et bannière ne peuvent donc pas
 * se contredire sur le FAIT, puisqu'elles descendent du même verdict.
 *
 * Corollaire : rien ici n'est calculé. Aucun seuil, aucune date reconstituée,
 * aucun `Date.now()`. `retard_jours` est affiché tel qu'il est servi, jamais
 * recalculé depuis `derniere_mesure` : un âge recalculé côté client dériverait
 * du verdict au fil de la journée et finirait par contredire la bannière.
 *
 * ## Ce que la carte a le droit d'AFFIRMER, et ce qu'elle laisse à la bannière
 *
 * La carte dit le FAIT (« en retard de 11 jours »), jamais le JUGEMENT
 * (« ce retard est anormal »). La bascule « attente normale / retard confirmé »
 * appartient à api-metier (`retardConfirme`, seuil dérivé d'`expected_lag_days`
 * avec un facteur et un plancher qui vivent là-bas) : la recopier ici en
 * ferait une deuxième source de vérité, qui divergerait au premier ajustement
 * du seuil et donnerait deux verdicts opposés sur le même écran. La pastille
 * reste donc neutre sur le retard et n'alarme (`warn`) que là où le verdict
 * NOMME lui-même un blocage.
 *
 * ## « Actif » n'est plus gratuit
 *
 * `a_jour === true` est désormais nécessaire pour l'afficher. Quand le bloc est
 * NON INSTRUIT (`status` absent, `null`, ou hors contrat : service enedis
 * injoignable, timeout de 5 s côté dapi, PRM hors parc), la carte ne dit pas
 * « Actif », et elle ne se tait pas non plus : elle affiche une pastille NEUTRE,
 * « État de collecte indisponible ». Une carte muette laisse le lecteur croire
 * qu'il n'y a rien à savoir, ce qui est le défaut même que ce lot corrige, en
 * plus discret. Cette pastille ne nomme aucune cause et ne promet rien : aucun
 * champ servi ne soutiendrait l'un ou l'autre. Elle dit exactement ce que dapi
 * dit en rendant `null` : nous ne savons pas.
 */

import type { StatusCollectePdl } from "@types"

export type { StatusCollectePdl }

/** Tag prêt à poser sur la carte, au format attendu par `MeterCardHeader`. */
export type CollecteTag = {
    severity: "success" | "info" | "warn"
    label: string
    icon: string
    tooltip: string
    /**
     * Clé i18n retenue, exposée pour les tests et le débogage : elle dit QUEL
     * état a été reconnu, indépendamment de la copie affichée.
     */
    key: CollecteTagKey
}

export type CollecteTagKey =
    | "collecte.a_jour"
    | "collecte.indisponible"
    | "collecte.demande_en_cours"
    | "collecte.interrompue"
    | "collecte.retard"
    | "collecte.premieres_mesures"

/**
 * Causes qui NOMMENT un blocage : la donnée n'arrivera pas tant que la cause
 * tient. Recopie EXACTE de l'ensemble publié par enedis
 * (`substrate/projection_client.CAUSES_BLOQUANTES`, contrat W §3.4). C'est le
 * seul discriminant utilisé pour séparer « en retard » de « interrompue » : un
 * seuil local ferait de la carte une deuxième autorité sur la fraîcheur.
 *
 * ⚠️ L'ensemble des causes est OUVERT côté enedis. Une cause inédite n'est donc
 * PAS bloquante ici, et retombe sur le fait nu (« en retard de n jours »), qui
 * reste vrai quelle que soit la cause. C'est la retombée sûre : affirmer une
 * interruption qu'on n'a pas lue serait le symétrique du défaut corrigé.
 */
export const CAUSES_BLOQUANTES = [
    "collecte_interrompue",
    "absence_instruite",
    "souscription_absente",
    "pdl_hors_service",
    "consentement_absent",
    "souscription_cdc_absente",
    "blocage_contractuel",
    "identifiant_inconnu",
    // 27/10 (SGT589) : Enedis ne peut plus relever le compteur à distance. La donnée
    // n'arrivera pas tant que la cause tient, c'est exactement la définition ci-dessus,
    // et le libellé qui en sort (« Collecte interrompue chez Enedis », acteur `enedis`)
    // est vrai mot pour mot. ⚠️ Recopie exacte : enedis doit l'ajouter aussi à SON
    // `CAUSES_BLOQUANTES` (absente le 27/10 sur `feat/refonte-enedis-2026`, la cause
    // elle-même n'y étant pas encore publiée).
    "compteur_non_teleoperable",
] as const

/** Cause servie quand une demande de récupération court vraiment. */
const CAUSE_MISE_A_JOUR_DEMANDEE = "mise_a_jour_demandee"

/** Suffixe de libellé par acteur (`a_qui_dagir`), ensemble FERMÉ côté enedis. */
const CLE_PAR_ACTEUR: Record<string, string> = {
    enedis: "enedis",
    homeys: "homeys",
    client: "client",
}

type Traducteur = (
    key: string,
    params?: Record<string, unknown>,
    count?: number
) => string

/**
 * Pastille NEUTRE de l'état non instruit. Volontairement sans cause et sans
 * échéance : le seul fait disponible est qu'aucun fait ne l'est.
 *
 * @param t - Traducteur i18n du composant appelant
 * @returns Le tag « État de collecte indisponible »
 */
const tagIndisponible = (t: Traducteur): CollecteTag => ({
    severity: "info",
    label: t("tag.collecte.indisponible"),
    icon: "pi pi-question-circle",
    tooltip: t("tooltip.tag.collecte.indisponible"),
    key: "collecte.indisponible",
})

/**
 * Traduit le bloc `data_retrieval.status` en une pastille de collecte.
 *
 * @param status - Bloc servi par dapi, ou `null`/`undefined` s'il est absent
 * @param t - Traducteur i18n du composant appelant
 * @returns Le tag à poser. Jamais `null` : un bloc non instruit rend la
 *   pastille neutre plutôt que rien.
 */
export const collecteTagPdl = (
    status: StatusCollectePdl | null | undefined,
    t: Traducteur
): CollecteTag => {
    // 0. Rien d'instruit : on le DIT, neutrement. Ni cause, ni délai, ni
    //    « Actif ». C'est la traduction fidèle du `null` de dapi.
    if (!status || typeof status !== "object") return tagIndisponible(t)

    // 1. À jour : le SEUL cas où « Actif » est vrai.
    if (status.a_jour === true) {
        return {
            severity: "success",
            label: t("tag.active"),
            icon: "pi pi-check-circle",
            tooltip: t("tooltip.tag.collecte.a_jour"),
            key: "collecte.a_jour",
        }
    }

    // 2. Une demande court vraiment. Deux faits servis pour la même chose : la
    //    cause projetée, et la preuve (`demande_en_cours`). L'un suffit.
    if (
        status.cause === CAUSE_MISE_A_JOUR_DEMANDEE ||
        Boolean(status.demande_en_cours)
    ) {
        return {
            severity: "info",
            label: t("tag.collecte.demande_en_cours"),
            icon: "pi pi-clock",
            tooltip: t("tooltip.tag.collecte.demande_en_cours"),
            key: "collecte.demande_en_cours",
        }
    }

    // 3. Le verdict NOMME un blocage : on le dit, et on dit à qui d'agir.
    if (
        typeof status.cause === "string" &&
        (CAUSES_BLOQUANTES as readonly string[]).includes(status.cause)
    ) {
        const acteur = CLE_PAR_ACTEUR[String(status.a_qui_dagir ?? "")] ?? null
        return {
            severity: "warn",
            label: acteur
                ? t(`tag.collecte.interrompue_${acteur}`)
                : t("tag.collecte.interrompue"),
            icon: "pi pi-exclamation-triangle",
            tooltip: acteur
                ? t(`tooltip.tag.collecte.interrompue_${acteur}`)
                : t("tooltip.tag.collecte.interrompue"),
            key: "collecte.interrompue",
        }
    }

    // 4. Le fait nu : l'âge de la dernière mesure, servi par le verdict.
    if (Number.isFinite(status.retard_jours as number)) {
        const jours = status.retard_jours as number
        return {
            severity: "info",
            label: t("tag.collecte.retard", { jours }, jours),
            icon: "pi pi-clock",
            tooltip: t("tooltip.tag.collecte.retard"),
            key: "collecte.retard",
        }
    }

    // 5. Aucun âge parce qu'aucune mesure n'est jamais arrivée : c'est une
    //    attente, pas un décrochage, et le dire évite de laisser la carte muette
    //    sur un compteur qui n'a encore rien produit.
    if (!status.derniere_mesure) {
        return {
            severity: "info",
            label: t("tag.collecte.premieres_mesures"),
            icon: "pi pi-clock",
            tooltip: t("tooltip.tag.collecte.premieres_mesures"),
            key: "collecte.premieres_mesures",
        }
    }

    // 6. Bloc incohérent (une dernière mesure sans âge, un champ hors contrat) :
    //    on ne choisit pas à sa place, et on ne se tait pas davantage que pour un
    //    bloc absent. Les deux disent la même chose au lecteur : nous ne savons
    //    pas. Ils rendent donc la même pastille.
    return tagIndisponible(t)
}

/**
 * Le tag d'ÉTAT que la carte compteur pose sur un PDL dont ni le consentement
 * ni le contrat ne signalent déjà quelque chose (révoqué, obsolète, non signé,
 * résilié, inexistant : ces cas nomment leur cause et gardent leur tag).
 *
 * C'est ici que « Actif » se gagne, et il se gagne aux TROIS conditions à la
 * fois : point en service, consentement actif, et collecte à jour. Avant
 * DEV-1620, les deux premières suffisaient, et un compteur décroché depuis 11
 * jours portait un badge vert.
 *
 * @param entree - État contractuel, état du consentement, bloc de collecte
 * @param t - Traducteur i18n du composant appelant
 * @returns Le tag à poser, ou `null` quand rien n'est affirmable
 */
export const tagEtatPdl = (
    entree: {
        etat?: string | null
        consentement?: string | null
        status?: StatusCollectePdl | null
    },
    t: Traducteur
): CollecteTag | null => {
    const tag = collecteTagPdl(entree.status, t)
    const contratEtConsentementSains =
        entree.etat === "en_service" && entree.consentement === "active"

    if (tag.key === "collecte.a_jour") {
        // Collecte à jour mais contrat ou consentement hors des rails : « Actif »
        // serait faux, et aucun autre libellé de collecte ne dit ce cas. On se
        // tait, le tag de consentement du composant parle à notre place.
        return contratEtConsentementSains ? tag : null
    }

    return tag
}
